import React from 'react';
import { View } from 'react-native';

import { IconAdd, IconRemove, Pressable, createMqStyles, tokens, useMqSelect } from '@fhs/ui';

import { LocateMeButton } from './locate-me-button';

interface MapControlsProps {
  onZoomIn: () => void;
  onZoomOut: () => void;
  onRecenter: () => void;
}

/**
 * Zoom pair and locate-me control, stacked at the bottom-right of the map per the design.
 * Both are centred on the same axis, which is why the narrower zoom pair sits slightly
 * further from the map edge than the wider circle.
 */
export const MapControls: React.VFC<MapControlsProps> = ({ onZoomIn, onZoomOut, onRecenter }) => {
  const styles = useMapControlsMqStyles();
  const iconSize = useMqSelect({ $gteDesktop: 24 }, 18);

  return (
    <View style={styles.container} pointerEvents="box-none">
      <View style={styles.zoomGroup}>
        <Pressable
          testID="map-zoom-in-button"
          accessibilityLabel="Zoom in"
          dd-action-name="store locator zoom in"
          onPress={onZoomIn}
          borderRadius={0}
          style={styles.zoomButton}
          // The design draws these at 24pt on small screens, so they need slop to clear the
          // 44pt minimum touch target. Omit the inner edge so the stacked buttons don't steal
          // taps from each other.
          hitSlop={{ top: 12, left: 12, right: 12 }}
        >
          <IconAdd size={iconSize} color={tokens.colors.$houseDark} />
        </Pressable>

        <View style={styles.zoomDivider} />

        <Pressable
          testID="map-zoom-out-button"
          accessibilityLabel="Zoom out"
          dd-action-name="store locator zoom out"
          onPress={onZoomOut}
          borderRadius={0}
          style={styles.zoomButton}
          hitSlop={{ bottom: 12, left: 12, right: 12 }}
        >
          <IconRemove size={iconSize} color={tokens.colors.$houseDark} />
        </Pressable>
      </View>

      <LocateMeButton onRecenter={onRecenter} />
    </View>
  );
};

const useMapControlsMqStyles = createMqStyles({
  container: {
    $base: {
      alignItems: 'center',
      bottom: 16,
      gap: 8,
      position: 'absolute',
      right: 16,
    },
    $gteDesktop: {
      bottom: 32,
      gap: 32,
      right: 32,
    },
  },
  zoomGroup: {
    $base: {
      backgroundColor: tokens.colors.$white,
      borderRadius: 8,
      elevation: 4,
      overflow: 'hidden',
      shadowColor: tokens.colors.$black,
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.15,
      shadowRadius: 4,
    },
  },
  zoomButton: {
    $base: {
      alignItems: 'center',
      height: 24,
      justifyContent: 'center',
      width: 24,
    },
    $gteDesktop: {
      height: 32,
      width: 32,
    },
  },
  zoomDivider: {
    $base: {
      backgroundColor: tokens.colors.$black10,
      height: 1,
    },
  },
});
